import { Clock, MessageSquare, X, Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { mastersAPI, WorkflowStatus } from '@/api/mastersApi';
import { StatusDropdown } from '../StatusDropdown';
import { Task } from '@/types';
// moved React hooks import above
import { Loader } from '../Loader';
import { useAppSelector } from '@/store/hooks';

interface TaskModalHeaderProps {
  task: Task;
  onStatusUpdate: (newStatus: string) => void;
  commentsLength?: number;
  projectKey?:string;
  onTitleUpdate?: (newTitle: string) => Promise<void>; // Add this prop
  workflowStatuses?: Array<{ id: string; name: string }>;
  isViewerOnly?: boolean;
  projectId?:string;
}

export const TaskModalHeader: React.FC<TaskModalHeaderProps> = ({ 
  task, 
  onStatusUpdate ,
  commentsLength,projectKey,
  onTitleUpdate,
  workflowStatuses = [],
  isViewerOnly ,
  projectId
}) => {
    const [localStatuses, setLocalStatuses] = useState<WorkflowStatus[]>(workflowStatuses as any);

    // Keep local copy in sync with prop updates
    useEffect(() => {
      setLocalStatuses(workflowStatuses as any);
    }, [workflowStatuses]);

    // Fallback: load from API if not provided
    useEffect(() => {
      (async () => {
        if (!localStatuses || localStatuses.length === 0) {
          try {
            const data = await mastersAPI.getWorkflowStatuses();
            if (Array.isArray(data) && data.length > 0) {
              setLocalStatuses(data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) as any);
            }
          } catch {}
        }
      })();
    }, [localStatuses?.length]);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editedTitle, setEditedTitle] = useState(task.title);
    const [isUpdatingTitle, setIsUpdatingTitle] = useState(false);
    const titleInputRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null); 
    const { currentProject } = useAppSelector((state) => state.projects);
    const taskId = `${projectKey?projectKey:'AHM'}-${(task as any).display_number ?? task.id?.substring(0, 6)}`;
    const handleStatusUpdate = async (statusId: string) => {
      setIsLoading(true);
      try {
        await onStatusUpdate(statusId);
      } catch (error) {
        console.error('Failed to update status:', error);
      } finally {
        setIsLoading(false);
      }
    };
      const autoResizeTextarea = () => {
    if (titleInputRef.current) {
      titleInputRef.current.style.height = 'auto';
      titleInputRef.current.style.height = titleInputRef.current.scrollHeight + 'px';
    }
  };

  // === CHANGED: Auto-resize when editing starts or content changes ===
  useEffect(() => {
    if (isEditingTitle) {
      autoResizeTextarea();
    }
  }, [isEditingTitle, editedTitle]);
  
    useEffect(() => {
      if (!isEditingTitle) {
        setEditedTitle(task.title);
      }
    }, [task.title, isEditingTitle]);

    const handleTitleClick = () => {
      if (onTitleUpdate && !isEditingTitle) {
        setIsEditingTitle(true);
        setEditedTitle(task.title);
      }
    };
 // === ADDED: Focus input when editing starts ===
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

 
useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (isEditingTitle && 
            containerRef.current && 
            !containerRef.current.contains(event.target as Node)) {
          handleTitleSave();
        }
      };

      if (isEditingTitle) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }
    }, [isEditingTitle, editedTitle]);
  // === ADDED: Handle title save ===
  const handleTitleSave = async () => {
    if (!editedTitle.trim() || editedTitle === task.title || !onTitleUpdate) {
      setIsEditingTitle(false);
      return;
    }
    setIsUpdatingTitle(true);
    try {
      await onTitleUpdate(editedTitle.trim());
      setIsEditingTitle(false);
    } catch (error) {
      console.error('Failed to update title:', error);
    } finally {
      setIsUpdatingTitle(false);
    }
  };

  // === ADDED: Handle title cancel ===
  const handleTitleCancel = () => {
    setIsEditingTitle(false);
    setEditedTitle(task.title);
  };

   const handleTitleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) { // === CHANGED: Added !e.shiftKey to allow Shift+Enter for new lines ===
        e.preventDefault(); // === ADDED: Prevent default to avoid new line ===
        handleTitleSave();
      } else if (e.key === 'Escape') {
        handleTitleCancel();
      }
    };
  
  return (
    <div className="mb-6" ref={containerRef}>
      {isLoading && <Loader variant="overlay" size="lg" text={'Updating...'} />}
      {isUpdatingTitle && <Loader variant="overlay" size="lg" text={'Updating title...'} />}

      <div className="flex items-start justify-between mb-2">
         <div className="flex-1 min-w-0">
          {/* === CHANGED: Title section with edit functionality === */}
          <div  className="flex items-start gap-3 mb-1 group w-full">
            {isEditingTitle && !isViewerOnly ? (
              <div className="flex flex-col gap-2 w-full">
                <textarea
                  ref={titleInputRef}
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  className="text-2xl font-bold text-gray-900 border-b-2 border-blue-500 focus:outline-none flex-1 bg-transparent px-1 resize-none min-h-[60px] break-words" 
                  disabled={isViewerOnly || isUpdatingTitle}
                  rows={1}
                  style={{ minHeight: '40px' }}
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleTitleSave}
                    disabled={isUpdatingTitle}
                    className="p-1.5 hover:bg-green-100 rounded text-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleTitleCancel}
                    disabled={isUpdatingTitle}
                     className="p-1.5 hover:bg-red-100 rounded text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h1 
                  onClick={handleTitleClick}
                  className="text-2xl font-bold text-gray-900 break-words whitespace-pre-wrap min-w-0 flex-1 leading-relaxed cursor-pointer hover:bg-gray-50 rounded px-1 transition-colors"
                >
                  {task.title}
                </h1>
              </>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span className="font-medium">{taskId}</span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Created {new Date(task.created_at || Date.now()).toLocaleDateString()}
            </span>
            <span className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              {commentsLength} comments
            </span>
          </div>
        </div>
        {(() => {
          // Prefer strict ID-based selection; fall back to name match only for legacy tasks
          const byId = String((task as any).workflow_status_id || '');
          const byName = (localStatuses.find(ws => ws.name?.toLowerCase() === String((task as any).status || '').toLowerCase())?.id) || '';
          const currentId = byId || byName || '';
          return (
            <div className="w-[220px]">
              {localStatuses.length > 0 ? (
                <StatusDropdown
                  disabled={isViewerOnly}
                  value={currentId}
                  options={localStatuses}
                  onChange={(statusId) => handleStatusUpdate(statusId)}
                  placeholder="Select Status"
                  size="sm"
                />
              ) : (
                <div className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-500">Loading statuses…</div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};